import React, { useState, useEffect, useCallback } from 'react';

const TRANSITION_DURATION = 500;

export default function Testimonials({ items = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState(0); // -1 for left, 1 for right
  const [isAnimating, setIsAnimating] = useState(false);

  const nextIndex = (currentIndex + 1) % items.length;
  const prevIndex = (currentIndex - 1 + items.length) % items.length;

  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(timer);
  }, [isAutoPlaying, currentIndex]);

  const handleNext = useCallback(() => {
    if (isAnimating) return;
    setDirection(1);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(nextIndex);
      setIsAnimating(false);
    }, TRANSITION_DURATION);
  }, [nextIndex, isAnimating]);

  const handlePrev = useCallback(() => {
    if (isAnimating) return;
    setDirection(-1);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(prevIndex);
      setIsAnimating(false);
    }, TRANSITION_DURATION);
  }, [prevIndex, isAnimating]);

  const handleDotClick = (index) => {
    if (isAnimating) return;
    setDirection(index > currentIndex ? 1 : -1);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(false);
    }, TRANSITION_DURATION);
    setIsAutoPlaying(false);
  };

  if (!items.length) return null;

  return (
    <div className="max-w-5xl mx-auto px-4">
      {/* Main testimonial carousel */}
      <div
        className="relative bg-white shadow-sm rounded-2xl border border-neutral-100 overflow-hidden"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        <div className="relative min-h-[200px] px-8 py-8">
          {/* Content slider */}
          <div className="relative">
            <div
              className={
                'transition-all duration-500 transform ' +
                (isAnimating
                  ? direction > 0
                    ? '-translate-x-full opacity-0'
                    : 'translate-x-full opacity-0'
                  : 'translate-x-0 opacity-100')
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                {/* Author info */}
                <div className="flex flex-col items-center order-2 md:order-1">
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-wood/20 to-wood/10 flex items-center justify-center border-2 border-white shadow-md">
                        {items[currentIndex].gender === 'female' ? (
                          <svg viewBox="0 0 24 24" className="w-8 h-8 text-wood">
                            <path
                              fill="currentColor"
                              d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"
                            />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" className="w-8 h-8 text-wood">
                            <path
                              fill="currentColor"
                              d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6M12,13C14.67,13 20,14.33 20,17V20H4V17C4,14.33 9.33,13 12,13M12,14.9C9.03,14.9 5.9,16.36 5.9,17V18.1H18.1V17C18.1,16.36 14.97,14.9 12,14.9Z"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="absolute inset-0 rounded-full border-2 border-wood/20"></div>
                    </div>
                    <div className="mt-3 text-center">
                      <div className="font-medium text-neutral-900 text-sm">
                        {items[currentIndex].name}
                      </div>
                      <div className="text-sm text-primary">
                        {items[currentIndex].project}
                        {items[currentIndex].location && (
                          <span className="text-neutral-400 ml-2">
                            • {items[currentIndex].location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Testimonial text */}
                <div className="md:col-span-3 order-1 md:order-2">
                  {/* Star rating centered above text */}
                  <div className="flex justify-center mb-4">
                    <div className="inline-flex gap-0.5 px-3 py-1 bg-secondary/5 rounded-full">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className="w-4 h-4 text-secondary"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="text-base text-neutral-600 leading-relaxed">
                    "{items[currentIndex].text}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-50">
          <div
            className="h-full bg-primary/20 transition-all duration-[5000ms] ease-linear"
            style={{
              width: isAutoPlaying ? '100%' : '0%',
              transitionProperty: 'width',
            }}
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="relative mt-6">
        {/* Side navigation buttons */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0">
          <div className="container flex justify-between">
            <button
              onClick={handlePrev}
              className={
                'w-10 h-10 -ml-5 flex items-center justify-center rounded-full bg-white border border-neutral-200 shadow-sm transition-all ' +
                (isAnimating
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:border-primary hover:text-primary')
              }
              disabled={isAnimating}
              aria-label="Previous testimonial"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className={
                'w-10 h-10 -mr-5 flex items-center justify-center rounded-full bg-white border border-neutral-200 shadow-sm transition-all ' +
                (isAnimating
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:border-primary hover:text-primary')
              }
              disabled={isAnimating}
              aria-label="Next testimonial"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Bottom navigation dots */}
        <div className="flex justify-center">
          <div className="flex gap-2">
            {items.map((_, idx) => (
              <button
                key={idx}
                onClick={() => handleDotClick(idx)}
                className={
                  'w-2 h-2 rounded-full transition-all ' +
                  (idx === currentIndex ? 'bg-primary w-6' : 'bg-neutral-200 hover:bg-neutral-300')
                }
                aria-label={'Go to testimonial ' + (idx + 1)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
